/**
 * Created by Rodrigo Quesada on 30/03/17.
 */
import {PremedicalEducation} from '../../../../domain/model/education/premedical/premedical-education.model';
import {Ecfmg} from '../../../../domain/model/education/ecfmg/ecfmg.model';
import {MedicalEducation} from '../../../../domain/model/education/medical/medical-education.model';
import {OtherEducation} from '../../../../domain/model/education/other/other-education.model';
import {Internship} from '../../../../domain/model/education/internship/internship.model';
import {Residency} from '../../../../domain/model/education/residency/residency.model';
import {FellowshipOrPreceptorship} from '../../../../domain/model/education/fellowship-or-preceptorship/fellowship-or-preceptorship.model';
import {formatMonthYear, separatedBy, separatedBySpace, toOrderValue} from '../../../shared/utils';

//region Other Classes
export type EducationType = PremedicalEducation | Ecfmg | MedicalEducation | OtherEducation | Internship | Residency | FellowshipOrPreceptorship;

export enum EducationTypeId {
    PREMEDICAL = 1, ECFMG, MEDICAL, OTHER, INTERNSHIP, RESIDENCY, FELLOWSHIP_OR_PRECEPTORSHIP
}

abstract class EducationEntryData {
    type: EducationTypeId;
    name?: string;
    address?: string;
    dateFrom?: string;
    dateTo?: string;

    constructor(educationType: EducationEntryType<any>) {
        this.type = educationType.id;
    }
}

abstract class EducationEntryType<T extends EducationType> {

    constructor(readonly id: EducationTypeId, private readonly _modelType: new () => T) {
    }

    isType(model: EducationType): model is T { return model instanceof this._modelType; }

    abstract toEntryData(model: T): Partial<EducationEntryData>

    abstract orderValueFor(model: T): number
}

class PremedicalEducationEntryType extends EducationEntryType<PremedicalEducation> {

    constructor() { super(EducationTypeId.PREMEDICAL, PremedicalEducation); }

    toEntryData(model: PremedicalEducation): Partial<EducationEntryData> {
        const address = model.address;

        return {
            name: model.institution,
            address: separatedBy(', ', address.city, address.stateOrProvince)
        };
    }


    orderValueFor(model: PremedicalEducation): number { return toOrderValue(model.graduationDate); }
}

class EcfmgEducationEntryType extends EducationEntryType<Ecfmg> {

    constructor() { super(EducationTypeId.ECFMG, Ecfmg); }

    toEntryData(model: Ecfmg): Partial<EducationEntryData> {
        return {
            name: model.certificateNumber,
            dateTo: formatMonthYear(model.dateIssued)
        };
    }

    orderValueFor(model: Ecfmg): number { return toOrderValue(model.dateIssued); }
}

class MedicalEducationEntryType extends EducationEntryType<MedicalEducation> {

    constructor() { super(EducationTypeId.MEDICAL, MedicalEducation); }

    toEntryData(model: MedicalEducation): Partial<EducationEntryData> {
        const address = model.address;
        const attendanceDuration = model.attendance.duration;

        const data = <Partial<EducationEntryData>>{
            name: model.institution,
            address: separatedBySpace(separatedBy(', ', address.city, address.stateOrProvince), address.postalCode)
        };

        if (attendanceDuration) {
            data.dateFrom = formatMonthYear(attendanceDuration.from);
            data.dateTo = formatMonthYear(attendanceDuration.to);
        }

        return data;
    }

    orderValueFor(model: MedicalEducation): number {
        const attendanceDuration = model.attendance.duration;
        return toOrderValue(attendanceDuration ? attendanceDuration.to : undefined);
    }
}

class OtherEducationEducationEntryType extends EducationEntryType<OtherEducation> {

    constructor() { super(EducationTypeId.OTHER, OtherEducation); }

    toEntryData(model: OtherEducation): Partial<EducationEntryData> {
        const address = model.address;
        const attendanceDuration = model.attendance.duration;

        const data = <Partial<EducationEntryData>>{
            name: model.institution,
            address: separatedBySpace(separatedBy(', ', address.city, address.stateOrProvince), address.postalCode)
        };

        if (attendanceDuration) {
            data.dateFrom = formatMonthYear(attendanceDuration.from);
            data.dateTo = formatMonthYear(attendanceDuration.to);
        }

        return data;
    }

    orderValueFor(model: OtherEducation): number {
        const attendanceDuration = model.attendance.duration;
        return toOrderValue(attendanceDuration ? attendanceDuration.to : undefined);
    }
}

class InternshipEducationEntryType extends EducationEntryType<Internship> {

    constructor() { super(EducationTypeId.INTERNSHIP, Internship); }

    toEntryData(model: Internship): Partial<EducationEntryData> {
        const address = model.address;
        const attendanceDuration = model.attendance.duration;

        const data = <Partial<EducationEntryData>>{
            name: model.institution,
            address: separatedBySpace(separatedBy(', ', address.city, address.stateOrProvince), address.postalCode)
        };

        if (attendanceDuration) {
            data.dateFrom = formatMonthYear(attendanceDuration.from);
            data.dateTo = formatMonthYear(attendanceDuration.to);
        }

        return data;
    }

    orderValueFor(model: Internship): number {
        const attendanceDuration = model.attendance.duration;
        return toOrderValue(attendanceDuration ? attendanceDuration.to : undefined);
    }
}

class ResidencyEducationEntryType extends EducationEntryType<Residency> {

    constructor() { super(EducationTypeId.RESIDENCY, Residency); }

    toEntryData(model: Residency): Partial<EducationEntryData> {
        const address = model.address;
        const attendanceDuration = model.attendance.duration;

        const data = <Partial<EducationEntryData>>{
            name: model.institution,
            address: separatedBySpace(separatedBy(', ', address.city, address.stateOrProvince), address.postalCode)
        };

        if (attendanceDuration) {
            data.dateFrom = formatMonthYear(attendanceDuration.from);
            data.dateTo = formatMonthYear(attendanceDuration.to);
        }

        return data;
    }

    orderValueFor(model: Residency): number {
        const attendanceDuration = model.attendance.duration;
        return toOrderValue(attendanceDuration ? attendanceDuration.to : undefined);
    }
}

class FellowshipOrPreceptorshipEducationEntryType extends EducationEntryType<FellowshipOrPreceptorship> {

    constructor() { super(EducationTypeId.FELLOWSHIP_OR_PRECEPTORSHIP, FellowshipOrPreceptorship); }

    toEntryData(model: FellowshipOrPreceptorship): Partial<EducationEntryData> {
        const address = model.address;
        const attendanceDuration = model.attendance.duration;

        const data = <Partial<EducationEntryData>>{
            name: model.institution,
            address: separatedBySpace(separatedBy(', ', address.city, address.stateOrProvince), address.postalCode)
        };

        if (attendanceDuration) {
            data.dateFrom = formatMonthYear(attendanceDuration.from);
            data.dateTo = formatMonthYear(attendanceDuration.to);
        }

        return data;
    }

    orderValueFor(model: FellowshipOrPreceptorship): number {
        const attendanceDuration = model.attendance.duration;
        return toOrderValue(attendanceDuration ? attendanceDuration.to : undefined);
    }
}
//endregion

const educationTypes: EducationEntryType<any>[] = [
    new PremedicalEducationEntryType(),
    new EcfmgEducationEntryType(),
    new MedicalEducationEntryType(),
    new OtherEducationEducationEntryType(),
    new InternshipEducationEntryType(),
    new ResidencyEducationEntryType(),
    new FellowshipOrPreceptorshipEducationEntryType()
];

export class EducationEntry extends EducationEntryData {

    private _orderValue: number;

    constructor(education: EducationType) {
        const educationType = educationTypes.find(it => it.isType(education))!;
        super(educationType);

        Object.assign(this, educationType.toEntryData(education));

        this._orderValue = educationType.orderValueFor(education);
    }

    compareTo(other: EducationEntry) {
        //reverse order from latest/null to oldest
        return -(this._orderValue - other._orderValue);
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/pages/cv/download/document/education-entry.class.ts